import { z } from 'zod';

const kgInsightCategorySchema = z.enum([
  'company_catalyst',
  'market_catalyst',
  'earnings_recap',
  'earnings_preview',
]);

const kgInsightSubcategorySchema = z.enum([
  'corporate_actions_strategic_announcements',
  'management_leadership_changes',
  'financial_operational_updates',
  'legal_regulatory_events',
  'thematic',
  'macroeconomic_policy_changes',
  'geopolitical_global_events',
]);

const sentimentSchema = z.enum(['positive', 'negative', 'neutral']);
const geoSentimentSchema = z.enum([
  ...sentimentSchema.options.filter(option => option !== 'neutral'),
  'mixed',
  'warning',
]);

const themeTypeSchema = z.enum(['industry', 'financial', 'macro']);

const kgInsightSubjectSchema = z.object({
  type: z.enum(['entity', 'theme', 'geo']),
  id: z.string(),
  ref: z
    .object({
      type: z.string(),
      id: z.string(),
    })
    .optional(),
});

const heroSectionSchema = z.object({
  type: z.literal('hero'),
  data: z.object({
    snakes: z.array(z.string()).optional(),
    badges: z.array(z.object({ label: z.string(), value: z.string() })),
  }),
});

const takeawayTagSchema = z.object({
  type: z.string(),
  label: z.string(),
  kind: z.enum(['industry', 'financial', 'macro']),
  id: z.string().optional().describe('theme id'),
});

const takeawaySectionSchema = z.object({
  type: z.literal('takeaway'),
  data: z.object({
    cards: z.array(
      z.object({
        title: z.string(),
        subtitle: z.string(),
        tags: z.array(takeawayTagSchema),
      })
    ),
  }),
});

const quotesSectionSchema = z.object({
  type: z.literal('quotes'),
  data: z.object({
    cards: z.array(
      z.object({
        topic: z.string(),
        sentiment: sentimentSchema,
        quote: z.string(),
        speaker: z.object({ name: z.string(), title: z.string() }),
      })
    ),
  }),
});

const cohortColumnTypeSchema = z.enum(['text', 'entity']);

const cohortColumnKeySchema = z.string();

const cohortColumnSchema = z.object({
  key: cohortColumnKeySchema,
  label: z.string(),
  type: cohortColumnTypeSchema,
});

const entityCellSchema = z.object({ value: z.string() });
const textCellSchema = z.object({
  value: z.string(),
  sentiment: sentimentSchema.optional(),
});

const cohortCellByTypeSchema = z.object({
  entity: entityCellSchema,
  text: textCellSchema,
});

const cohortCellSchema = z.union([entityCellSchema, textCellSchema]);

const cohortRowSchema = z.intersection(
  z.object({ asset: entityCellSchema }),
  z.record(cohortColumnKeySchema, cohortCellSchema.nullable())
);

const cohortTableSchema = z.object({
  type: z.literal('cohort_table'),
  data: z.object({
    theme_id: z.string(),
    sentiment: sentimentSchema,
    subtitle: z.string(),
    table: z.object({
      columns: z.array(cohortColumnSchema),
      rows: z.array(cohortRowSchema),
    }),
    read: z.string(),
    alfred_ctas: z.array(z.string()),
  }),
});

const graphSectionSchema = z.object({
  type: z.literal('graph'),
  data: z.object({ cohorts: z.array(cohortTableSchema) }),
});

const geoSectionSchema = z.object({
  type: z.literal('geo'),
  data: z.object({
    subtitle: z.string(),
    cards: z.array(
      z.object({
        name: z.string(),
        code: z.string(),
        sentiment: geoSentimentSchema,
        description: z.string(),
      })
    ),
    read: z.string(),
    alfred_ctas: z.array(z.string()),
  }),
});

const detailsGridSectionSchema = z.object({
  type: z.literal('details_grid'),
  data: z.object({
    title: z.string(),
    subtitle: z.string(),
    cards: z.array(
      z.object({
        label: z.string(),
        value: z.string(),
        value_details: z.string().optional(),
        sentiment: sentimentSchema.optional(),
      })
    ),
  }),
});

const kgInsightSectionSchema = z.discriminatedUnion('type', [
  heroSectionSchema,
  detailsGridSectionSchema,
  takeawaySectionSchema,
  quotesSectionSchema,
  graphSectionSchema,
  geoSectionSchema,
]);

const kgInsightSchema = z.object({
  id: z.string(),
  category: kgInsightCategorySchema,
  subcategory: kgInsightSubcategorySchema.optional(),
  sentiment: sentimentSchema,
  title: z.string(),
  subtitle: z.string(),
  subject: kgInsightSubjectSchema,
  sections: z.array(kgInsightSectionSchema),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

export type KgInsightCategory = z.infer<typeof kgInsightCategorySchema>;
export type KgInsightSubcategory = z.infer<typeof kgInsightSubcategorySchema>;
export type KgInsightSubject = z.infer<typeof kgInsightSubjectSchema>;
export type KgInsightSection = z.infer<typeof kgInsightSectionSchema>;
export type KgInsightHeroSection = z.infer<typeof heroSectionSchema>;
export type KgInsightTakeawaySection = z.infer<typeof takeawaySectionSchema>;
export type KgInsightQuotesSection = z.infer<typeof quotesSectionSchema>;
export type KgInsightGraphSection = z.infer<typeof graphSectionSchema>;
export type KgInsightGeoSection = z.infer<typeof geoSectionSchema>;
export type KgInsightDetailsGridSection = z.infer<
  typeof detailsGridSectionSchema
>;
export type KgInsightCohortTable = z.infer<typeof cohortTableSchema>;
export type KgInsightCohortColumn = z.infer<typeof cohortColumnSchema>;
export type KgInsightCohortCell = z.infer<typeof cohortCellSchema>;
export type KgInsightCohortRow = z.infer<typeof cohortRowSchema>;
export type KgInsightEntityCell = z.infer<typeof entityCellSchema>;
export type KgInsightTextCell = z.infer<typeof textCellSchema>;
export type KgInsightCohortCellByType = z.infer<typeof cohortCellByTypeSchema>;
export type KgInsight = z.infer<typeof kgInsightSchema>;
export type KgInsightSentiment = z.infer<typeof sentimentSchema>;
export type KgInsightGeoSentiment = z.infer<typeof geoSentimentSchema>;
export type KgInsightTakeawayTag = z.infer<typeof takeawayTagSchema>;
export type KgInsightThemeType = z.infer<typeof themeTypeSchema>;

const kgInsightFilterSubjectSchema = z.object({
  type: kgInsightSubjectSchema.shape.type,
  id: z.string(),
});

const kgInsightFilterSubjectRefSchema = z.object({
  type: z.literal('earnings'),
  id: z.string(),
});

export const kgInsightFilterRequestSchema = z.object({
  categories: z.array(kgInsightCategorySchema).optional(),
  subcategories: z.array(kgInsightSubcategorySchema).optional(),
  subjects: z.array(kgInsightFilterSubjectSchema).optional(),
  subject_refs: z.array(kgInsightFilterSubjectRefSchema).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().optional(),
  page_size: z.number().optional(),
});

const kgInsightSummarySchema = z.object({
  id: z.string(),
  category: kgInsightCategorySchema,
  subcategory: kgInsightSubcategorySchema.optional(),
  subject: kgInsightSubjectSchema,
  title: z.string(),
  subtitle: z.string(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});

const kgInsightFilterResponseSchema = z.array(kgInsightSummarySchema);

export type KgInsightFilterRequest = z.infer<
  typeof kgInsightFilterRequestSchema
>;
export type KgInsightFilterResponse = z.infer<
  typeof kgInsightFilterResponseSchema
>;

export const kgInsights = {
  postKgInsightFilter: {
    path: 'kg-insights/v1/filter',
    schema: kgInsightFilterResponseSchema,
  },
  getKgInsight: {
    path: (insightId: string) => `kg-insights/v1/${insightId}`,
    schema: kgInsightSchema,
  },
};
